import { DelayFormValues } from 'assets/js/pages/engage/workflows/actions/types';
import { defaultCampaignSelectValue } from 'assets/js/pages/engage/workflows/components/CampaignSelect';
import { defaultWidgetSelectValue } from 'assets/js/pages/engage/workflows/components/WidgetSelect';
import { FormData } from 'assets/js/pages/engage/workflows/types';
import { WorkflowStarter } from 'assets/js/pages/engage/workflows/utils/starterItems';
import { formDefaults } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import {
    MessageTemplateSlugEnum,
    SuggestedWorkflowTypeEnum,
    WorkflowActionTypeEnum,
    WorkflowTriggerTypeEnum,
    WorkflowUtilityNodeTypeEnum,
} from 'constants/workflows';
import { DateTime } from 'luxon';
import { EngageMessageTemplate } from 'types/domain';
import { v4 as uuid } from 'uuid';

const sharedNodeAttributes = {
    position: { x: 0, y: 0 },
    selected: false,
    selectable: false,
};

const defaultDelayFormData: FormData<DelayFormValues> = {
    ...formDefaults,
    values: {
        preset: '1 hour',
        custom_value: '1',
        custom_unit: 'day',
        custom_time: DateTime.now().startOf('hour'),
        skip_weekends: false,
    },
};

const getNodesFromSelectedPreset = (
    selectedItem:
        | WorkflowStarter<SuggestedWorkflowTypeEnum, 'suggested'>
        | WorkflowStarter<WorkflowTriggerTypeEnum, 'trigger'>,
    messageTemplates: EngageMessageTemplate[],
): NodeWithData[] => {
    if (selectedItem.type === 'trigger') {
        return [
            {
                ...sharedNodeAttributes,
                id: uuid(),
                data: {
                    type: selectedItem.slug,
                    parentId: null,
                },
                type: selectedItem.slug,
                selectable: true,
                selected: true,
            },
        ];
    }

    switch (selectedItem.slug) {
        case SuggestedWorkflowTypeEnum.ABANDONED_DONATION_EMAIL: {
            const triggerId = uuid();
            const delayId = uuid();
            const emailId = uuid();
            const template = messageTemplates.find(
                t => t.slug === MessageTemplateSlugEnum.ABANDONED_DONATION_EMAIL,
            );

            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM,
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: delayId,
                    type: WorkflowUtilityNodeTypeEnum.DELAY,
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.DELAY,
                        formData: defaultDelayFormData,
                        parentId: triggerId,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: emailId,
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    data: {
                        type: WorkflowActionTypeEnum.SEND_EMAIL,
                        formData: {
                            ...formDefaults,
                            values: getFormDataValuesFromTemplate(template),
                        },
                        parentId: delayId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.DONATIONS_OVER_100_EMAIL: {
            const triggerId = uuid();
            const delayId = uuid();
            const emailId = uuid();
            const template = messageTemplates.find(
                t => t.slug === MessageTemplateSlugEnum.DONATIONS_OVER_100_EMAIL,
            );

            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.DONATION_CREATED,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.DONATION_CREATED,
                        formData: {
                            ...formDefaults,
                            values: {
                                campaigns: defaultCampaignSelectValue,
                                donationOperator: 'gt',
                                donationAmount: '100',
                                donationAmountEnd: '',
                            },
                        },
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: delayId,
                    type: WorkflowUtilityNodeTypeEnum.DELAY,
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.DELAY,
                        formData: defaultDelayFormData,
                        parentId: triggerId,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: emailId,
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    data: {
                        type: WorkflowActionTypeEnum.SEND_EMAIL,
                        formData: {
                            ...formDefaults,
                            values: getFormDataValuesFromTemplate(template),
                        },
                        parentId: delayId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.FIRST_TIME_SUPPORTERS_EMAIL: {
            const triggerId = uuid();
            const delayId = uuid();
            const emailId = uuid();
            const template = messageTemplates.find(
                t => t.slug === MessageTemplateSlugEnum.FIRST_TIME_SUPPORTERS_EMAIL,
            );

            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION,
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: delayId,
                    type: WorkflowUtilityNodeTypeEnum.DELAY,
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.DELAY,
                        formData: defaultDelayFormData,
                        parentId: triggerId,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: emailId,
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    data: {
                        type: WorkflowActionTypeEnum.SEND_EMAIL,
                        formData: {
                            ...formDefaults,
                            values: getFormDataValuesFromTemplate(template),
                        },
                        parentId: delayId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.LAST_GIFT_MADE_EMAIL: {
            const triggerId = uuid();
            const delayId = uuid();
            const emailId = uuid();
            const template = messageTemplates.find(
                t => t.slug === MessageTemplateSlugEnum.LAST_GIFT_MADE_EMAIL,
            );

            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: delayId,
                    type: WorkflowUtilityNodeTypeEnum.DELAY,
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.DELAY,
                        formData: defaultDelayFormData,
                        parentId: triggerId,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: emailId,
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    data: {
                        type: WorkflowActionTypeEnum.SEND_EMAIL,
                        formData: {
                            ...formDefaults,
                            values: getFormDataValuesFromTemplate(template),
                        },
                        parentId: delayId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.RECURRING_DONOR_EMAIL: {
            const triggerId = uuid();
            const delayId = uuid();
            const emailId = uuid();
            const template = messageTemplates.find(
                t => t.slug === MessageTemplateSlugEnum.RECURRING_DONOR_EMAIL,
            );

            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.PLAN_CREATED,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.PLAN_CREATED,
                        formData: {
                            ...formDefaults,
                            values: {
                                campaigns: defaultCampaignSelectValue,
                                frequency: 'monthly',
                                recurringOperator: 'gt',
                                recurringAmount: '100',
                                recurringAmountEnd: '',
                            },
                        },
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: delayId,
                    type: WorkflowUtilityNodeTypeEnum.DELAY,
                    data: {
                        type: WorkflowUtilityNodeTypeEnum.DELAY,
                        formData: defaultDelayFormData,
                        parentId: triggerId,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: emailId,
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    data: {
                        type: WorkflowActionTypeEnum.SEND_EMAIL,
                        formData: {
                            ...formDefaults,
                            values: getFormDataValuesFromTemplate(template),
                        },
                        parentId: delayId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.TAG_OVER_1000: {
            const triggerId = uuid();
            const tagId = uuid();
            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.DONATION_CREATED,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.DONATION_CREATED,
                        formData: {
                            ...formDefaults,
                            values: {
                                campaigns: defaultCampaignSelectValue,
                                donationOperator: 'gt',
                                donationAmount: '1000',
                                donationAmountEnd: '',
                            },
                        },
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: tagId,
                    type: WorkflowActionTypeEnum.CONTACT_TAG,
                    data: {
                        type: WorkflowActionTypeEnum.CONTACT_TAG,
                        formData: {
                            ...formDefaults,
                            values: {
                                action: 'tag',
                                tags: ['Top Supporter'],
                            },
                        },
                        parentId: triggerId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.SIGNUP_WIDGET_SUBSCRIBER_EMAIL: {
            const triggerId = uuid();
            const emailId = uuid();
            const template = messageTemplates.find(
                t => t.slug === MessageTemplateSlugEnum.SIGNUP_WIDGET_SUBSCRIBER_EMAIL,
            );

            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.WIDGET_SUBSCRIBED,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.WIDGET_SUBSCRIBED,
                        formData: {
                            ...formDefaults,
                            values: {
                                widgets: defaultWidgetSelectValue,
                            },
                        },
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: emailId,
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    data: {
                        type: WorkflowActionTypeEnum.SEND_EMAIL,
                        formData: {
                            ...formDefaults,
                            values: getFormDataValuesFromTemplate(template),
                        },
                        parentId: triggerId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.MAJOR_DONOR_TASK: {
            const triggerId = uuid();
            const taskCreateId = uuid();
            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.DONATION_CREATED,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.DONATION_CREATED,
                        formData: {
                            ...formDefaults,
                            values: {
                                campaigns: defaultCampaignSelectValue,
                                donationOperator: 'gt',
                                donationAmount: '1000',
                                donationAmountEnd: '',
                            },
                        },
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: taskCreateId,
                    type: WorkflowActionTypeEnum.TASK_CREATE,
                    data: {
                        type: WorkflowActionTypeEnum.TASK_CREATE,
                        formData: {
                            ...formDefaults,
                            values: {
                                title: 'Follow up with major donor',
                                description:
                                    '<p>Call the donor expressing gratitude for their generous donation</p>',
                                assigned_to: [],
                                due_at: null,
                                status: 'todo',
                            },
                        },
                        parentId: triggerId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.FIRST_TIME_DONOR_TASK: {
            const triggerId = uuid();
            const taskCreateId = uuid();
            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION,
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: taskCreateId,
                    type: WorkflowActionTypeEnum.TASK_CREATE,
                    data: {
                        type: WorkflowActionTypeEnum.TASK_CREATE,
                        formData: {
                            ...formDefaults,
                            values: {
                                title: 'Follow up with first time donor',
                                description:
                                    '<p>Call the donor expressing gratitude for their generous donation</p>',
                                assigned_to: [],
                                due_at: null,
                                status: 'todo',
                            },
                        },
                        parentId: triggerId,
                    },
                },
            ];
        }
        case SuggestedWorkflowTypeEnum.PAST_DONOR_TASK: {
            const triggerId = uuid();
            const taskCreateId = uuid();
            return [
                {
                    ...sharedNodeAttributes,
                    id: triggerId,
                    type: WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
                    selectable: true,
                    selected: true,
                    data: {
                        type: WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION,
                        formData: {
                            ...formDefaults,
                            values: {
                                campaigns: defaultCampaignSelectValue,
                                interval: '6 months',
                                customAmount: '',
                                customUnit: 'year',
                            },
                        },
                        parentId: null,
                    },
                },
                {
                    ...sharedNodeAttributes,
                    id: taskCreateId,
                    type: WorkflowActionTypeEnum.TASK_CREATE,
                    data: {
                        type: WorkflowActionTypeEnum.TASK_CREATE,
                        formData: {
                            ...formDefaults,
                            values: {
                                title: 'Follow up with past donor',
                                description:
                                    '<p>This donor hasn’t given in the last six months. Reach out and try to gauge interest in donating again.</p>',
                                assigned_to: [],
                                due_at: null,
                                status: 'todo',
                            },
                        },
                        parentId: triggerId,
                    },
                },
            ];
        }

        default: {
            return [];
        }
    }
};

const getFormDataValuesFromTemplate = (template?: EngageMessageTemplate) => {
    return {
        body: template?.body ?? '',
        internal_name: template?.name ?? '',
        subject: template?.subject ?? '',
        message_template_id: template?.id,
    };
};

export { getNodesFromSelectedPreset };
